import React, { memo, useEffect, useRef, useState } from "react";
import { css, useTheme } from "@emotion/react";
import ReactPlayer from "react-player";
import debounce from "lodash/debounce";
import IgnoreErrorBoundary from "core/Components/Errors/IgnoreErrorBoundary";
import window from 'core/includes/window';
import browser from "core/includes/browser";

const PLAYER_CONFIG = {
    file: {
        attributes: {
            controlsList: "nodownload",
            onContextMenu: (event: React.MouseEvent) => event.preventDefault(),
        },
        forceDASH: true,
    }
};

type Props = {
    autoPlay?: boolean,
    onOverlayClick?: () => void,
    onPause?: () => void,
    onPlay?: () => void,
    onStart?: () => void,
    overlay?: boolean,
    playlistUrl?: string,
    ratio?: number,
    videos: string[],
};

const VideoPlayer: React.FC<Props> = ({
    autoPlay = false,
    onOverlayClick,
    onPause,
    onPlay,
    onStart,
    overlay = false,
    playlistUrl,
    ratio = 10 / 16,
    videos,
}) => {
    const theme = useTheme();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const playerRef = useRef<ReactPlayer | null>(null);
    const isIOS = browser?.getOS().name === "iOS";

    const videoFiles = isIOS || !playlistUrl ? videos : playlistUrl;

    const onWindowResize = debounce(() => {
        setWindowWidth(window.innerWidth);
    }, 50);

    useEffect(() => {
        window.addEventListener('resize', onWindowResize);

        return () => window.removeEventListener('resize', onWindowResize);
    }, [onWindowResize]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const playerStyle = (width: number, ratio: number) => css`
        position: relative;
        left: 50%;
        top: 50%;
        background-color: ${theme.colours.black};
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        box-shadow: ${theme.borderAndShadow.boxShadow};
        outline: none;

        * {
            outline: none;

            &:focus {
                outline: none;
            }
        }
    `;

    const overlayStyle = css`
        background-color: rgba(0, 0, 0, 0.6);
        position: fixed;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: ${theme.zIndex.zIndexHighest - 1};
    `;

    return (
        <IgnoreErrorBoundary>
            {overlay && (
                <div css={overlayStyle} onClick={onOverlayClick} />
            )}

            <div css={playerStyle(windowWidth, ratio)}>
                <ReactPlayer
                    ref={playerRef}
                    url={videoFiles}
                    controls={true}
                    config={PLAYER_CONFIG}
                    width={"100%"}
                    height={"100%"}
                    playing={autoPlay}
                    onStart={onStart}
                    onPlay={onPlay}
                    onPause={onPause}
                />
            </div>
        </IgnoreErrorBoundary>
    );
};

export default memo(VideoPlayer);
